import { Input, Space, Row, Upload, Spin, message, Modal } from "antd";
import { useEffect, useRef, useState } from "react";
import Recorder from "recorder-core";
import { flushSync } from "react-dom";
import { RcFile } from "antd/es/upload";
import { nanoid } from "nanoid";

import "recorder-core/src/extensions/frequency.histogram.view";
import "recorder-core/src/extensions/lib.fft";
import "recorder-core/src/engine/mp3";
import "recorder-core/src/engine/mp3-engine";

import styles from "./index.less";

import { AskAIType } from "../FlowEditPage/utils/streamFetch";
import { AllowedAccept, getAskAIType } from "./utils";
import { postUploadFile } from "../FlowEditPage/services/app";
import { ResponseCode } from "../constants";

import KeyBoardIcon from "../assets/imgs/debug-chat/keyboard.png";
import VoiceIcon from "../assets/imgs/debug-chat/speak.png";
import UploadIcon from "../assets/imgs/debug-chat/upload.png";
import ClearIcon from "../assets/imgs/debug-chat/clear.png";
import MicrofrontIcon from "../assets/imgs/debug-chat/microfont_icon.png";
import SendIcon from "../assets/imgs/debug-chat/send.png";
import SendDisabledIcon from "../assets/imgs/debug-chat/send_disabled.png";

type ChatInputProps = {
  value: string;
  onEnter: (value: string, type: AskAIType, extraInfo?: any) => void;
  onChange: (value: string) => void;
};
const ChatInput = ({ value, onEnter, onChange }: ChatInputProps) => {
  const recorderRef = useRef<any>();
  const waveRef = useRef<any>();
  const [inputType, setInputType] = useState<"text" | "voice">("text");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    recorderRef.current = Recorder({
      onProcess: function (
        buffers: any,
        powerLevel: any,
        bufferDuration: any,
        bufferSampleRate: any
        // newBufferIdx: any,
      ) {
        waveRef.current?.input(
          buffers[buffers.length - 1],
          powerLevel,
          bufferSampleRate
        );
      },
    });
  }, []);

  function beginRecord() {
    waveRef.current = Recorder?.FrequencyHistogramView?.({
      elem: "#recwave",
      stripeEnable: false,
      lineCount: 6,
      position: 0,
      minHeight: 1,
      widthRatio: 0.3,
      stripeHeight: 1,
      mirrorEnable: true,
      linear: [0, "#cc9e6c", 1, "#cc9e6c"],
    });

    recorderRef.current.start();
  }

  /**
   * 结束录音，直接生成文件上传
   */
  async function stopSpeak() {
    const waitUrl = new Promise<string>((resolve, reject) => {
      recorderRef.current?.stop(
        async function (blob: any, duration: any) {
          console.log(
            blob,
            (window.URL || webkitURL).createObjectURL(blob),
            "时长:" + duration + "ms"
          );
          const url = (window.URL || webkitURL).createObjectURL(blob);
          console.log("本地播放链接为 ", url);
          console.log(
            "已录制mp3：" +
              duration +
              "ms " +
              blob.size +
              "字节，可以点击播放、上传了",
            2
          );

          try {
            const file = new File([blob], `record_${nanoid()}.mp3`, {
              type: blob.type,
            });
            const rsp = await postUploadFile(file);
            if (rsp.code === ResponseCode.Success) {
              if (!!rsp.data.url) {
                resolve(rsp.data.url);
              } else {
                console.error("未返回 url");
                reject();
                return;
              }
            }
          } catch (err) {
            console.error("文件上传失败", err);
            reject();
          }
        },
        function (msg: any) {
          console.log("录音失败:" + msg, 1);
          reject();
        }
      );
    });

    return await waitUrl;
  }

  async function handleUpload(file: RcFile) {
    // const { size } = file;
    // if (size > 50 * 1024 * 1024) {
    //   message.warning("文件大小不能超过50M");
    //   return;
    // }
    setUploading(true);
    const type = getAskAIType(file.type, file.name);
    try {
      const rsp = await postUploadFile(file);
      if (rsp.code === ResponseCode.Success) {
        if (!!rsp.data.url && !!type) {
          // 触发 ai 事件
          onEnter(rsp.data.url, type, { name: file.name });
        } else {
          console.error("未返回 url");
        }
      }
      setUploading(false);
    } catch (err) {
      setUploading(false);
      console.error(err);
    }
  }

  function handleSpeak() {
    flushSync(() => {
      setIsSpeaking(true);
    });
    beginRecord();
  }

  async function handleSendVoice() {
    setIsSpeaking(false);
    const url = await stopSpeak();
    onEnter(url, "VOICE");
  }

  function renderActionIcon() {
    if (inputType === "text") {
      return (
        <img
          src={VoiceIcon}
          className={styles.icon}
          onClick={() => {
            setInputType("voice");
            recorderRef.current.open(
              () => {
                message.success("开启麦克风成功");
              },
              function (msg: any, isUserNotAllow: any) {
                //用户拒绝未授权或不支持
                // console.log(msg, isUserNotAllow);
                if (!isUserNotAllow) {
                  Modal.error({
                    title: "错误",
                    content: "无法录音，请检查是否开启录音权限",
                  });
                  stopSpeak();
                }
                setInputType("text");
              }
            );
          }}
        />
      );
    }
    return (
      <img
        src={KeyBoardIcon}
        className={styles.icon}
        onClick={() => {
          setIsSpeaking(false);
          stopSpeak();
          setInputType("text");
        }}
      />
    );
  }

  function renderSpeak() {
    if (!isSpeaking) {
      return (
        <Row
          className={styles["voice-input"]}
          justify="center"
          align="middle"
          style={{ color: "#c1854e", fontWeight: "bold" }}
          onClick={handleSpeak}
        >
          <img src={MicrofrontIcon} className={styles.micofront} />
          点击向我提问
        </Row>
      );
    }
    return (
      <Row
        className={`${styles["voice-input"]} ${styles["record-input"]}`}
        onClick={handleSendVoice}
      >
        提问结束 点击发送
        <div id="recwave" className={styles["recording-icon"]} />
        <img
          src={ClearIcon}
          className={styles.clear}
          onClick={(e) => {
            e.stopPropagation();
            setIsSpeaking(false);
            stopSpeak();
          }}
        />
      </Row>
    );
  }

  return (
    <div className={styles["chat-input"]}>
      {renderActionIcon()}
      {inputType === "text" && (
        <div className={styles["text-input"]}>
          <Input.TextArea
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13 && !e.shiftKey) {
                e.preventDefault();
                if (value) {
                  onEnter(value, "TEXT");
                }
                return;
              }
            }}
            autoSize={{ minRows: 1, maxRows: 10 }}
            className={styles.textarea}
          />
          <Space style={{ height: "100%" }}>
            <img
              src={value ? SendIcon : SendDisabledIcon}
              className={styles["send-icon"]}
              onClick={() => {
                if (value) {
                  onEnter(value, "TEXT");
                }
              }}
            />
          </Space>
        </div>
      )}
      {inputType === "voice" && renderSpeak()}
      <Upload
        beforeUpload={handleUpload}
        showUploadList={false}
        maxCount={1}
        accept={AllowedAccept}
        customRequest={() => {}}
      >
        <Spin spinning={uploading}>
          <img src={UploadIcon} className={styles.icon} />
        </Spin>
      </Upload>
    </div>
  );
};

export default ChatInput;
